import {
  AdjustmentsHorizontalIcon,
  Bars4Icon,
  DocumentDuplicateIcon,
  PencilSquareIcon,
  TrashIcon,
} from '@heroicons/react/24/outline'
import { GlobalConfigType } from 'actions'
import cloneDeep from 'clone-deep'
import { getLoanInputFields } from 'config/loan.input.fields.constants'
import { IVisibleLogic } from 'config/loan.input.visibility.type'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import {
  deleteApplicationEmailTemplate,
  duplicateApplicationEmailTemplate,
  enableApplicationEmailTemplate,
  getApplicationEmailTemplate,
  submitApplicationEmailTemplate,
  updateApplicationEmailTemplateVisibleLogic,
} from 'services'
import { svgLoading } from 'stories/assets'
import { Button, Toggle, Tooltip } from 'stories/components'
import { confirm } from 'utils'
import { promptVisibleLogic } from 'utils/modals/promptVisibleLogic'

import { EditApplicationEmailTemplateModal } from './EditApplicationEmailTemplateModal'

export const ApplicationEmailTemplate = () => {
  const { loanFieldProperties } = useSelector((state: any) => ({
    loanFieldProperties: state.globalConfigReducer[GlobalConfigType.LoanFields],
  }))

  const [isLoading, setIsLoading] = useState(false)
  const [data, setData] = useState<Array<any>>([])
  const [editingItem, setEditingItem] = useState<{ id: number; name?: string; subject?: string } | null>(null)

  useEffect(() => {
    refresh()
  }, [])

  const refresh = () => {
    setIsLoading(true)
    getApplicationEmailTemplate()
      .then(({ data }) => {
        setData(data)
      })
      .finally(() => setIsLoading(false))
  }

  const onSaveTemplate = async (id: number, payload: { name: string; subject: string }) => {
    await submitApplicationEmailTemplate(id, payload)
    refresh()
  }

  const onEdit = (id: number) => {
    window.open(`/condition_template/applicationEmailTemplate/${id}`)
  }

  const onTrash = async (id: number) => {
    const result = await confirm('Do you want to remove this template?')
    if (!result) return

    setIsLoading(true)
    deleteApplicationEmailTemplate(id)
      .then(refresh)
      .catch(() => setIsLoading(false))
  }

  const onDuplicate = async (id: number) => {
    const result = await confirm('Are you sure you want to duplciate this email template?')
    if (!result) return
    await duplicateApplicationEmailTemplate(id)
    refresh()
  }

  const onEnable = async (id: number) => {
    setIsLoading(true)
    const res = await enableApplicationEmailTemplate(id)
    if (res.success) {
      const temp = cloneDeep(data)
      temp.map((item) => {
        if (item.id === id) {
          item.enabled = !item.enabled
        }
      })
      setData(temp)
    }
    setIsLoading(false)
  }

  const onEditVisibleLogic = async (item: { id: number; name: string; visibleLogic?: IVisibleLogic[][] }) => {
    const result = (await promptVisibleLogic(`Visible Conditions - ${item.name}`, {
      loanFieldProperties,
      fieldInputs: getLoanInputFields(),
      value: item.visibleLogic || [],
      noLogicMessage: 'No conditions set. This template applies to all loans when enabled.',
      hasNotVisible: true,
    })) as IVisibleLogic[][] | false
    if (result === false) return

    setIsLoading(true)
    await updateApplicationEmailTemplateVisibleLogic(item.id, result as any)
    refresh()
  }

  return (
    <div className="relative">
      <h2 className="text-2xl font-bold flex items-center mb-3">
        Loan Application Email Templates
        {isLoading && (
          <span className="ml-3">
            <img src={svgLoading} className="inline w-6 h-6 text-white animate-spin" />
          </span>
        )}
      </h2>
      <div className="flex flex-wrap justify-end mb-3">
        <div className="w-32">
          <Button full onClick={() => setEditingItem({ id: 0 })}>
            Add
          </Button>
        </div>
      </div>

      <div className="relative overflow-x-auto shadow-md sm:rounded-lg">
        <table className="w-full text-sm text-left text-gray-900 dark:text-gray-400 pl-6">
          <thead className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide bg-slate-50 border-b border-slate-200 dark:bg-gray-700 dark:text-gray-400">
            <tr>
              <th scope="col" className="px-3 py-3">
                No
              </th>
              <th scope="col" className="py-3 px-3">
                Name
              </th>
              <th scope="col" className="py-3 px-3">
                Subject
              </th>
              <th scope="col" className="py-3 px-3">
                Enabled
              </th>
              <th scope="col" className="px-3">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="">
            {data.map((item: any, index) => {
              const hasVisibleLogic = item.visibleLogic && item.visibleLogic.length > 0
              return (
                <tr className={`border-b ${index % 2 ? 'bg-slate-50' : 'bg-white'}`} key={`${index}`}>
                  <td scope="row" className="px-3 py-3 font-medium text-gray-900 dark:text-white whitespace-nowrap">
                    {index + 1}
                  </td>
                  <td className="px-3">{item.name}</td>
                  <td className="px-3">{item.subject || '-'}</td>
                  <td className="px-3">
                    <Toggle
                      id={`enabled-${item.id}`}
                      title=""
                      value={item.enabled}
                      onChange={() => onEnable(item.id)}
                    />
                  </td>
                  <td className="px-3">
                    <span className="flex">
                      <Tooltip message="Edit Name & Subject">
                        <span
                          className="text-shade-blue p-1 hover-shadow1 cursor-pointer"
                          onClick={() => setEditingItem({ id: item.id, name: item.name, subject: item.subject })}
                        >
                          <PencilSquareIcon className="w-4 h-4" />
                        </span>
                      </Tooltip>
                      <Tooltip message="Edit Email">
                        <span
                          className="text-shade-blue  p-1 hover-shadow1 cursor-pointer"
                          onClick={() => onEdit(item.id)}
                        >
                          <Bars4Icon className="w-4 h-4" />
                        </span>
                      </Tooltip>
                      <Tooltip message="Duplicate">
                        <span
                          className="text-shade-blue  p-1 hover-shadow1 cursor-pointer"
                          onClick={() => onDuplicate(item.id)}
                        >
                          <DocumentDuplicateIcon className="w-4 h-4" />
                        </span>
                      </Tooltip>
                      <Tooltip message="Delete">
                        <span
                          className="text-red-800 p-1 hover-shadow1 cursor-pointer"
                          onClick={() => onTrash(item.id)}
                        >
                          <TrashIcon className="w-4 h-4" />
                        </span>
                      </Tooltip>
                      <Tooltip message={hasVisibleLogic ? 'Has visible conditions' : 'Set visible conditions'}>
                        <span
                          className={`p-1 hover-shadow1 cursor-pointer rounded ${
                            hasVisibleLogic ? 'text-shade-blue' : 'text-gray-400'
                          }`}
                          onClick={() => onEditVisibleLogic(item)}
                        >
                          <AdjustmentsHorizontalIcon className="w-4 h-4" />
                        </span>
                      </Tooltip>
                    </span>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {editingItem !== null && (
        <EditApplicationEmailTemplateModal
          item={editingItem.id ? editingItem : null}
          onClose={() => setEditingItem(null)}
          onSubmit={onSaveTemplate}
        />
      )}
    </div>
  )
}
